import React from 'react';
import serverErrorImg from '../../assets/error.png';
import { useTranslation } from 'react-i18next';

// Add custom styles for animations
const styles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes bounceIn {
    0% { opacity: 0; transform: scale(0.3); }
    50% { opacity: 1; transform: scale(1.05); }
    70% { transform: scale(0.9); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes glitch {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-2px); }
    40% { transform: translateX(2px); }
    60% { transform: translateX(-1px); }
    80% { transform: translateX(1px); }
  }
  .animate-fadeIn { animation: fadeIn 0.6s ease-out; }
  .animate-bounceIn { animation: bounceIn 0.8s ease-out; }
  .animate-glitch { animation: glitch 0.3s ease-in-out infinite; }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

const ServerError = () => {
    const { t } = useTranslation();

    return (
        <div className="flex justify-center items-center bg-gradient-to-br from-red-50 via-pink-50 to-purple-50 animate-fadeIn">
            <div className="max-w-lg w-full bg-white/95 backdrop-blur-xl shadow-2xl rounded-3xl overflow-hidden transform animate-bounceIn border border-white/20">
                {/* Error Header with gradient */}
                <div className="bg-gradient-to-r from-red-600 to-pink-600 p-6 text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-white/10 transform -skew-y-2"></div>
                    <div className="relative z-10">
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg animate-glitch">
                            <svg className="w-12 h-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-2">
                            🚫 Server Error
                        </h2>
                        <p className="text-red-100 text-lg">We're experiencing technical difficulties</p>
                    </div>
                </div>

                <div className="p-8 text-center">
                    {/* Enhanced Error Icon */}
                    <div className="relative mb-8">
                        <div className="absolute inset-0 bg-red-200 rounded-full animate-ping opacity-20"></div>
                        <img 
                            src={serverErrorImg} 
                            alt="Server Error" 
                            className="relative mx-auto h-32 w-32 transform hover:scale-110 transition-transform duration-300"
                        />
                    </div>

                    {/* Enhanced Error Message */}
                    <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-6 mb-8">
                        <h3 className="text-xl font-bold text-gray-800 mb-3">{t('server_error')}</h3>
                        <p className="text-gray-600 text-lg mb-4">
                            {t('server_error_message')}
                        </p>
                        <div className="bg-white rounded-xl p-4 border border-red-200">
                            <div className="flex items-center mb-3">
                                <svg className="w-6 h-6 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <h4 className="font-bold text-gray-800">What happened?</h4>
                            </div>
                            <ul className="text-left space-y-2 text-gray-700">
                                <li className="flex items-center">
                                    <svg className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    Our servers are temporarily unavailable
                                </li>
                                <li className="flex items-center">
                                    <svg className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    We're working to fix this issue
                                </li>
                                <li className="flex items-center">
                                    <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                    </svg>
                                    Please try again in a few minutes
                                </li>
                            </ul>
                        </div>
                    </div>
                    
                    <button 
                        onClick={() => window.location.reload()}
                        className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 px-6 rounded-2xl font-bold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-blue-300/50 mb-4"
                    >
                        <div className="flex items-center justify-center">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                            Try Again
                        </div>
                    </button>
                    
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                        <div className="flex items-center justify-center">
                            <svg className="w-5 h-5 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <p className="text-gray-600 text-sm font-medium">Error persists? Contact our support team</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServerError;